import { Component, Input, OnInit, SimpleChanges, AfterViewInit, ElementRef, ChangeDetectorRef, HostListener, OnDestroy, input, output, viewChild, signal, effect, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { VideoTrimService } from '@services/video-trim.service';
import { RecordingService } from '@services/recording.service';
import { DeleteRunwayService } from '@services/delete-runway.service';
import { StorageService } from '@services/storage.service';
import { UserService } from '@services/user.service';
import { SwUpdate } from '@angular/service-worker';
import { Subscription } from 'rxjs';
import { ToastService } from '@services/toast.service';
import { ref, uploadBytes, getDownloadURL, getBytes } from '@angular/fire/storage';
import { Storage } from '@angular/fire/storage';
import { collection, query, where, orderBy, limit, getDocs } from '@angular/fire/firestore';
import { Firestore } from '@angular/fire/firestore';
import { environment } from '@environments/environment';

const DEBUG_MODE = false;

@Component({
  selector: 'app-videoedit',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, FormsModule],
  templateUrl: './videoedit.component.html'
})
export class VideoEditComponent implements OnInit, AfterViewInit, OnDestroy {
  readonly videoDuration = input<number>(0);
  readonly currentTime = input<number>(0);
  readonly videoUrl = input<string | null>(null); // Input URL (remote or blob)
  readonly originalFilename = input<string>('');
  readonly readOnly = input<boolean>(false);
  readonly croppedVideoSaved = output<{
    start: number;
    end: number;
    url: string;
    duration: number;
    filename: string; // This should be the storage path
  }>();
  readonly seekTo = output<number>();
  readonly videoElement = viewChild.required<ElementRef<HTMLVideoElement>>('videoElement');
  readonly fileInput = viewChild.required<ElementRef<HTMLInputElement>>('fileInput');
  readonly sliderContainer = viewChild<ElementRef<HTMLDivElement>>('sliderContainer');

  readonly videoMetadataLoaded = output<void>();
  readonly videoTimeUpdate = output<void>();

  timeForm: FormGroup;
  isVideoLoaded: boolean = false;
  trimmedVideoUrl: string | null = null;
  showTrimmedAlert: boolean = false;
  trimmedDuration: number | null = null;

  loaded = false; // Tracks if VideoTrimService (FFmpeg) is loaded
  startTime = 0;
  endTime = 0;

  selectedFile: File | null = null;
  gifUrl: string | null = null;
  private subscription: Subscription | null = null;

  dragging: 'start' | 'end' | 'current' | null = null;
  sliderRect: DOMRect | null = null;

  isPlaying: boolean = false;
  currentTimePosition: number = 0;

  videoFile: File | null = null; // For direct file uploads

  downloadURL: string | null = null;

  showTrimControls = false;
  showInfoAlert = true;

  precisionTrim: boolean = false;

  readonly videoTrimmed = output<{
    url: string; // Final URL (potentially local dev URL)
    filename: string; // Storage path
  }>();

  // New state variable for Trim button
  trimButtonState: 'idle' | 'loading' | 'performing' = 'idle';

  // **New Variables for Original Start and End Times**
  originalStartTime: number = 0;
  originalEndTime: number = 0;

  // **New Variables for Trim Button Enablement and Tooltip**
  showTooltip: boolean = false; // To control tooltip visibility

  // Add this new property
  savingVideo: boolean = false;

  // Add property for long trim message
  showLongTrimMessage: boolean = false;
  private longTrimMessageTimeout: any = null;

  // Add writable signals for internal state
  private _duration = signal<number>(0);
  private _time = signal<number>(0);
  private _url = signal<string | null>(null); // Internal URL, might be object URL

  // Add computed values for read-only inputs
  private readonly _computedDuration = computed(() => this.videoDuration());
  private readonly _computedTime = computed(() => this.currentTime());
  private readonly _computedUrl = computed(() => this.videoUrl()); // Use input URL for effect trigger

  // Update onTimeUpdate to handle destroyed state
  private isDestroyed = false;

  private resizeObserver: ResizeObserver | null = null;

  private _objectUrls: string[] = []; // Track created object URLs for cleanup
  private _video = signal<HTMLVideoElement | null>(null);
  private _currentVideoBlob: Blob | null = null; // Store the fetched/uploaded blob

  constructor(
    private fb: FormBuilder,
    private cd: ChangeDetectorRef,
    private videoTrimService: VideoTrimService,
    private recordingService: RecordingService,
    private deleteRunwayService: DeleteRunwayService,
    private storageService: StorageService,
    private userService: UserService,
    private swUpdate: SwUpdate,
    private toastService: ToastService,
    private storage: Storage,
    private firestore: Firestore
  ) {
    this.timeForm = this.fb.group({
      startTime: ['00:00', [Validators.required, Validators.pattern(/^([0-5][0-9]):([0-5][0-9])$/)]],
      endTime: ['00:00', [Validators.required, Validators.pattern(/^([0-5][0-9]):([0-5][0-9])$/)]]
    });

    // Initialize signals
    this._url = signal<string | null>(null);
    this._duration = signal<number>(0);
    this.isDestroyed = false;

    // Handle input changes with effects
    effect(() => {
      const duration = this.videoDuration();
      if (duration !== undefined && isFinite(duration)) { // Check if finite
        this.log('Video duration input changed:', duration);
        // Only update endTime if it hasn't been manually set or is invalid
        if (this.endTime === 0 || !isFinite(this.endTime) || this.endTime === this.originalEndTime) {
            this.endTime = duration;
            this.originalEndTime = duration; // Update original only when duration changes
            this.updateEndTime(); // Update form and UI
        }
      }
    });

    // Effect to react to external videoUrl changes
    effect(() => {
      const externalUrl = this.videoUrl(); // Use the input signal
      this.log('External videoUrl input changed:', externalUrl);
      if (externalUrl) {
        this.processVideo(externalUrl); // Process the new external URL
      } else {
        this.clearVideo(); // Clear if external URL is null
      }
    });
  }

  ngOnInit() {
    this.log('ngOnInit called');
    // Initialize end time based on input duration if available
    const initialDuration = this.videoDuration();
    if (initialDuration && isFinite(initialDuration)) {
        this.endTime = initialDuration;
        this.originalEndTime = initialDuration;
    }
    this.originalStartTime = this.startTime; // Should be 0 initially
    this.updateTimeForm();

    // Subscribe to the loaded state of the VideoTrimService
    this.subscription = this.videoTrimService.getLoaded().subscribe(loaded => {
      this.loaded = loaded;
      if (loaded) {
        console.log('VideoEditComponent: VideoTrimService (FFmpeg) is loaded.');
      } else {
        console.log('VideoEditComponent: VideoTrimService (FFmpeg) is loading...');
      }
      this.cd.detectChanges(); // Ensure UI updates when status changes
    });
  }

  ngAfterViewInit() {
    // Add a retry mechanism for video element initialization
    const maxRetries = 10;
    let retryCount = 0;

    const initializeVideo = () => {
      const videoElement = this.videoElement();
      if (videoElement?.nativeElement) {
        console.log('Video element found:', videoElement.nativeElement);

        // Process video if URL is available from input
        const url = this.videoUrl(); // Use input signal directly
        if (url) {
          this.processVideo(url); // Process initial URL
        }

        // Initialize slider in next tick to ensure DOM is ready
        queueMicrotask(() => {
          // Only try to initialize slider if we're not in read-only mode
          if (!this.readOnly()) {
            this.initializeSlider();
          }
        });

        // Add event listeners directly
        videoElement.nativeElement.addEventListener('loadedmetadata', this.onVideoMetadataLoaded.bind(this));
        videoElement.nativeElement.addEventListener('timeupdate', this.onTimeUpdate.bind(this));

        // Force duration initialization if the video already has metadata
        if (videoElement.nativeElement.readyState >= 1 && videoElement.nativeElement.duration) {
          console.log('Video already has metadata in ngAfterViewInit');
          this.forceDurationInitialization(videoElement.nativeElement);
        }

      } else {
        retryCount++;
        if (retryCount < maxRetries) {
          // Retry after a short delay if video element is not found
          setTimeout(initializeVideo, 100);
        } else {
          console.warn('Video element not found after maximum retries');
        }
      }
    };

    // Start the initialization process
    initializeVideo();
  }

  private initializeSlider(): void {
    // Skip slider initialization if we're in read-only mode
    if (this.readOnly()) {
      return;
    }

    const sliderContainer = this.sliderContainer();
    if (!sliderContainer?.nativeElement) {
      // If slider container isn't available, try again in the next frame
      requestAnimationFrame(() => {
        if (!this.isDestroyed) {
          this.initializeSlider();
        }
      });
      return;
    }

    // Setup resize observer
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }

    this.resizeObserver = new ResizeObserver(() => {
      if (!this.isDestroyed) {
        this.repositionScrubbers();
      }
    });

    this.resizeObserver.observe(sliderContainer.nativeElement);
  }

  ngOnDestroy() {
    this.isDestroyed = true;

    // Cleanup subscriptions
    if (this.subscription) {
      this.subscription.unsubscribe();
    }

    // Cleanup form
    if (this.timeForm) {
      // Remove controls one by one to avoid emission errors
      Object.keys(this.timeForm.controls).forEach(key => {
        try {
          if (!this.isDestroyed) {
            this.timeForm.removeControl(key);
          }
        } catch (error) {
          console.warn(`Error removing control ${key}:`, error);
        }
      });
    }

    // Cleanup resize observer
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }

    // Remove event listeners
    const videoElement = this.videoElement();
    if (videoElement?.nativeElement) {
      videoElement.nativeElement.removeEventListener('loadedmetadata', this.onVideoMetadataLoaded.bind(this));
      videoElement.nativeElement.removeEventListener('timeupdate', this.onTimeUpdate.bind(this));
    }

    // Cleanup all blob URLs
    this.cleanupVideoResources();

    // Clear any pending timeouts
    if (this.longTrimMessageTimeout) {
      clearTimeout(this.longTrimMessageTimeout);
      this.longTrimMessageTimeout = null;
    }
  }

  // Add this method to safely remove form controls
  private safeRemoveControl(controlName: string) {
    if (this.isDestroyed || !this.timeForm) return;

    try {
      if (this.timeForm.contains(controlName)) {
        this.timeForm.removeControl(controlName);
      }
    } catch (error) {
      console.warn(`Error removing control ${controlName}:`, error);
    }
  }

  private log(...args: any[]): void {
    if (DEBUG_MODE) {
      console.log(...args);
    }
  }

  private logGroup(label: string): void {
    if (DEBUG_MODE) {
      console.group(label);
    }
  }

  private logGroupEnd(): void {
    if (DEBUG_MODE) {
      console.groupEnd();
    }
  }

  setupVideoElement() {
    this.logGroup('VideoEditComponent: setupVideoElement');
    const videoElement = this.videoElement();
    if (videoElement && videoElement.nativeElement) {
      const video = videoElement.nativeElement;
      this.log('Video Element Ready State:', video.readyState);
      this.log('Video Element Source:', video.src);
      this.log('Video Element Duration:', video.duration);
      // Event listeners are now added in ngAfterViewInit
    } else {
      this.log('Video Element: Not Available');
    }
    this.logGroupEnd();
  }

  // Renamed from processVideo to avoid confusion with input signal effect
  private async loadAndSetVideoSource(sourceUrl: string) {
    if (!sourceUrl) {
      console.log('No video source URL provided to loadAndSetVideoSource');
      this.clearVideo(); // Clear if URL is null/empty
      return;
    }

    const videoElement = this.videoElement();
    if (!videoElement?.nativeElement) {
      console.log('Video element not available for loading, retrying...');
      setTimeout(() => this.loadAndSetVideoSource(sourceUrl), 100);
      return;
    }

    // Revoke previous object URLs before creating a new one
    this.cleanupVideoResources();
    this._currentVideoBlob = null; // Reset blob state

    try {
      const video = videoElement.nativeElement;

      // Always fetch the source to get a manageable blob
      console.log('Fetching video data for processing:', sourceUrl);
      const response = await fetch(sourceUrl, {
        credentials: 'omit', // Important for CORS if fetching from different origins
        headers: {
          'Accept': 'video/mp4,video/webm,video/*;q=0.9,*/*;q=0.8'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status} for URL ${sourceUrl}`);
      }

      const blob = await response.blob();
      this._currentVideoBlob = blob; // Store the fetched blob
      console.log('Video blob stored, size:', this._currentVideoBlob.size);

      const objectUrl = URL.createObjectURL(blob);
      this._objectUrls.push(objectUrl); // Track for cleanup

      this._url.set(objectUrl); // Update internal URL signal
      video.crossOrigin = 'anonymous'; // Set before src
      video.src = objectUrl;
      video.load(); // Important: load the new source

      // onloadedmetadata listener added in ngAfterViewInit will handle duration init

      // Update component state
      this.isVideoLoaded = true; // Consider setting this in onloadedmetadata
      this._video.set(video);
      this.cd.detectChanges();

    } catch (error) {
      console.error('Error processing video:', error);
      this.toastService.show('Error loading video for editing.', 'error');
      this._currentVideoBlob = null; // Clear blob on error
      // Attempt to set the original URL directly as fallback (might fail CORS)
      const video = videoElement.nativeElement;
      video.crossOrigin = 'anonymous';
      video.src = sourceUrl;
      video.load();
      this._url.set(sourceUrl); // Update internal URL to the original one
    }
  }

  // Wrapper function called by the effect
  private processVideo(url: string) {
      this.loadAndSetVideoSource(url).catch(err => {
          console.error("Error in processVideo chain:", err);
      });
  }


  // Add a new method to force duration initialization
  private forceDurationInitialization(video: HTMLVideoElement): void {
    if (!video || !video.duration || !isFinite(video.duration)) { // Added isFinite check
      console.log('No video or valid duration available yet, retrying...');
      // Try again after a short delay
      setTimeout(() => {
        const videoElem = this.videoElement()?.nativeElement;
        if (videoElem) {
          this.forceDurationInitialization(videoElem);
        }
      }, 200); // Increased delay slightly
      return;
    }

    console.log('Forcing duration initialization with:', video.duration);

    // Set the duration in the component
    this._duration.set(video.duration);

    // Initialize times only if they haven't been set or are invalid
    if (this.startTime === 0 && this.endTime === 0) {
        this.endTime = video.duration;
        this.originalEndTime = video.duration;
    } else {
        // Ensure times are within bounds if already set
        this.startTime = Math.max(0, Math.min(this.startTime, video.duration));
        this.endTime = Math.max(this.startTime, Math.min(this.endTime, video.duration));
    }


    // Update form with correct times
    this.timeForm.patchValue({
      startTime: this.formatTime(this.startTime),
      endTime: this.formatTime(this.endTime)
    }, { emitEvent: false });

    // Update UI
    this.updateCurrentTimePosition();
    this.repositionScrubbers();

    // Emit the metadata loaded event
    this.videoMetadataLoaded.emit();

    // Force change detection
    this.cd.detectChanges();
  }

  private cleanupVideoResources() {
    // Cleanup existing video element source
    const currentVideo = this._video();
    if (currentVideo) {
      currentVideo.pause();
      currentVideo.removeAttribute('src'); // Remove src to release resource
      currentVideo.load(); // Ask the browser to load no source
    }

    // Revoke previously created object URLs
    this._objectUrls.forEach(url => {
      try {
        URL.revokeObjectURL(url);
        console.log('Revoked object URL:', url);
      } catch (e) {
        // Ignore errors from already revoked URLs
      }
    });
    this._objectUrls = []; // Clear the array
    this._currentVideoBlob = null; // Clear the stored blob
    this._url.set(null); // Clear internal URL state
  }


  private checkVideoLoaded() {
    this.logGroup('VideoEditComponent: checkVideoLoaded');
    const videoElement = this.videoElement();
    if (videoElement && videoElement.nativeElement) {
      const video = videoElement.nativeElement;
      this.log('Video element readyState:', video.readyState);
      this.log('Video element src:', video.src);

      if (video.readyState >= 2) {
        this.onVideoLoaded();
      } else {
        video.onloadeddata = () => this.onVideoLoaded();
      }
    } else {
      this.log('Video element not found');
    }
    this.logGroupEnd();
  }

  private onVideoLoaded() {
    this.logGroup('VideoEditComponent: onVideoLoaded');
    this.log('Video loaded');
    this.isVideoLoaded = true;
    this.cd.detectChanges();
    this.logVideoState();
    this.logGroupEnd();
  }

  onVideoMetadataLoaded(event?: Event): void { // Made event optional
    const videoElement = this.videoElement();
    if (!videoElement?.nativeElement) return;

    const duration = videoElement.nativeElement.duration;
    if (!duration || !isFinite(duration)) { // Added isFinite check
      console.error('Video duration not available or invalid on loadedmetadata');
      // Try to get duration after a short delay again
      setTimeout(() => {
        const videoElem = this.videoElement()?.nativeElement;
        if (videoElem) {
            const delayedDuration = videoElem.duration;
            if (delayedDuration && isFinite(delayedDuration)) {
              console.log('Got delayed duration on loadedmetadata:', delayedDuration);
              this.initializeWithDuration(delayedDuration);
            } else {
              console.error('Still no valid duration after delay on loadedmetadata.');
            }
        }
      }, 300); // Slightly longer delay
      return;
    }

    this.initializeWithDuration(duration);
  }

  private initializeWithDuration(duration: number): void {
    console.log('Initializing with duration:', duration);
    this._duration.set(duration);

    // Initialize times only if they haven't been set or are invalid
    if (this.startTime === 0 && this.endTime === 0) {
        this.endTime = duration;
        this.originalEndTime = duration;
    } else {
        // Ensure times are within bounds if already set
        this.startTime = Math.max(0, Math.min(this.startTime, duration));
        this.endTime = Math.max(this.startTime, Math.min(this.endTime, duration));
    }


    // Update form with correct times first
    this.timeForm.patchValue({
      startTime: this.formatTime(this.startTime),
      endTime: this.formatTime(this.endTime)
    }, { emitEvent: false });

    this.updateCurrentTimePosition();
    this.cd.detectChanges();

    // Add a small delay before initial positioning
    setTimeout(() => {
      this.repositionScrubbers();
    }, 100);

    // Emit the event to notify parent components
    this.videoMetadataLoaded.emit();
  }

  private updateEndTime() {
    this.log('Updating end time');
    const duration = this._duration();
    // Ensure end time doesn't exceed duration
    this.endTime = Math.min(this.endTime, duration);
    this.timeForm.patchValue({
      endTime: this.formatTime(this.endTime)
    }, { emitEvent: false });
    this.updateCurrentTimePosition();
    this.cd.detectChanges();
  }

  togglePlayPause() {
    this.log('togglePlayPause called');
    const videoElement = this.videoElement();
    if (!this.isVideoLoaded || !videoElement) return;

    const video = videoElement.nativeElement;
    if (this.isPlaying) {
      video.pause();
      this.log('Video paused');
      this.isPlaying = false;
    } else {
      // Ensure the video starts within the trim range
      if (video.currentTime < this.startTime || video.currentTime > this.endTime) {
        video.currentTime = this.startTime;
        this.log(`Video time set to startTime: ${this.startTime}`);
      }
      video.play();
      this.log('Video playing');
      this.isPlaying = true;
      this.startGreenBallMovement();
    }
    this.cd.detectChanges();
  }

  // Refactored trimVideo method (removed worker logic)
  async trimVideo() {
    // This method is now primarily a wrapper around handleTrim
    // It might be removed if handleTrim is called directly from the template event
    console.log("trimVideo called, deferring to handleTrim");
    await this.handleTrim();
  }

  // Helper method to extract filename from URL
  private extractFilenameFromUrl(url: string): string {
    // Try to extract from Firebase Storage URL
    const storageMatch = url.match(/\/o\/([^?]+)/);
    if (storageMatch && storageMatch[1]) {
      return decodeURIComponent(storageMatch[1]);
    }

    // Fall back to basic URL parsing
    const urlParts = url.split('/');
    const lastPart = urlParts[urlParts.length - 1];
    return lastPart.split('?')[0];
  }

  // Helper method to extract storage path from Firebase URL
  private extractPathFromUrl(url: string): string | null {
    const match = url.match(/\/o\/([^?]+)/);
    if (match && match[1]) {
      return decodeURIComponent(match[1]);
    }
    return null;
  }

  private updateTimeForm() {
    this.timeForm.patchValue({
      startTime: this.formatTime(this.startTime),
      endTime: this.formatTime(this.endTime)
    }, { emitEvent: false });
  }

  onTimeUpdate(event?: Event): void { // Made event optional
    if (this.isDestroyed) {
      return;
    }

    const video = this.videoElement()?.nativeElement;
    if (!video) return;

    // Get duration from video element first, fall back to signal if not available
    const duration = video.duration;
    if (!duration || !isFinite(duration)) { // Added isFinite check
      return; // Silently return instead of logging
    }

    const effectiveDuration = duration || this._duration();
    this._time.set(video.currentTime);
    const currentPercent = (video.currentTime / effectiveDuration) * 100;

    // Only update times if they haven't been initialized
    if (this.startTime === undefined) this.startTime = 0;
    if (this.endTime === undefined || this.endTime === 0 || !isFinite(this.endTime)) { // Added isFinite check
      this.endTime = effectiveDuration;
      this._duration.set(effectiveDuration);
    }

    if (video.currentTime >= this.endTime) {
      video.pause();
      video.currentTime = this.startTime;
      if (!this.isDestroyed) {
        this.seekTo.emit(this.startTime);
      }
      this.isPlaying = false;
      this.updateCurrentTimePosition();
      this.cd.detectChanges();
    } else {
      this.updateCurrentTimePosition();
    }

    if (this.isPlaying) {
      this.updateCurrentTimePosition();
    }

    if (!this.isDestroyed) {
      this.videoTimeUpdate.emit();
    }
  }

  onSliderChange(type: 'start' | 'end') {
    if (type === 'start') {
      this.startTime = Math.min(this.startTime, this.endTime);
    } else {
      this.endTime = Math.max(this.endTime, this.startTime);
    }
    this.cd.detectChanges();
  }

  startGreenBallMovement() {
    this.log('startGreenBallMovement called');
    this.updateCurrentTimePosition();
  }

  updateCurrentTimePosition() {
    if (!this.isVideoLoaded) {
      this.currentTimePosition = 0;
      this.log('Current time position set to 0 (video not loaded)');
      return;
    }

    const video = this.videoElement()?.nativeElement;
    if (!video) return;
    const currentTime = video.currentTime;
    const duration = this._duration();

    if (currentTime < this.startTime) {
      this.currentTimePosition = 0;
      this.log(`Current time position set based on currentTime < startTime: 0%`);
      return;
    }

    if (currentTime > this.endTime) {
      this.currentTimePosition = (this.endTime / duration) * 100;
      this.log(`Current time position set based on currentTime > endTime: ${this.currentTimePosition}%`);
      return;
    }

    this.currentTimePosition = (currentTime / duration) * 100;
    this.log(`Current time position updated to: ${this.currentTimePosition}%`);
  }

  seekToPosition(time: number): void {
    const videoElement = this.videoElement();
    if (videoElement?.nativeElement) {
      videoElement.nativeElement.currentTime = time;
      this.seekTo.emit(time);
    }
  }

  onTimeInputBlur(control: 'startTime' | 'endTime') {
    this.log(`onTimeInputBlur called for ${control}`);
    const value = this.timeForm.get(control)?.value;
    const timeInSeconds = this.parseTime(value);

    if (control === 'startTime') {
      this.startTime = Math.min(timeInSeconds, this.endTime);
      this.log(`startTime updated to: ${this.startTime}`);
    } else {
      this.endTime = Math.max(timeInSeconds, this.startTime);
      this.log(`endTime updated to: ${this.endTime}`);
    }

    this.updateCurrentTimePosition();
    this.cd.detectChanges();
  }

  onKeyDown(event: KeyboardEvent, control: 'startTime' | 'endTime') {
    if (event.key === 'Enter') {
      this.log(`Enter key pressed on ${control} input`);
      this.onTimeInputBlur(control);
    }
  }

  startDragging(type: 'start' | 'end' | 'current', event: MouseEvent) {
    this.log(`startDragging called with type=${type}`);
    event.preventDefault();
    this.dragging = type;

    const sliderContainer = this.sliderContainer();
    if (!sliderContainer?.nativeElement) {
      console.warn('Slider container not available for dragging');
      return;
    }

    this.sliderRect = sliderContainer.nativeElement.getBoundingClientRect();

    window.addEventListener('mousemove', this.onDrag);
    window.addEventListener('mouseup', this.stopDragging);
    this.log(`Dragging started: ${type}`);
  }

  onDrag = (event: MouseEvent) => {
    if (!this.dragging || !this.sliderRect) return;

    // Calculate position relative to slider
    const mouseX = Math.max(0, Math.min(event.clientX - this.sliderRect.left, this.sliderRect.width));
    const ratio = mouseX / this.sliderRect.width;
    const duration = this._duration();
    const newTime = ratio * duration;

    switch (this.dragging) {
      case 'start':
        this.startTime = Math.max(0, Math.min(newTime, this.endTime - 1));
        if (this.isVideoLoaded) {
          this.videoElement().nativeElement.currentTime = this.startTime;
        }
        break;
      case 'end':
        this.endTime = Math.min(duration, Math.max(newTime, this.startTime + 1));
        break;
      case 'current':
        if (this.isVideoLoaded) {
          const clampedTime = Math.min(Math.max(newTime, this.startTime), this.endTime);
          this.videoElement().nativeElement.currentTime = clampedTime;
        }
        break;
    }

    // Update form values
    this.timeForm.patchValue({
      startTime: this.formatTime(this.startTime),
      endTime: this.formatTime(this.endTime)
    }, { emitEvent: false });

    // Emit events if component is not destroyed
    if (!this.isDestroyed) {
      this.seekTo.emit(this.dragging === 'start' ? this.startTime : this.endTime);
    }

    // Reposition scrubbers
    this.repositionScrubbers();

    // Update UI
    this.updateCurrentTimePosition();
    this.cd.detectChanges();
  }

  stopDragging = () => {
    this.log('stopDragging called');
    this.dragging = null;
    this.sliderRect = null;
    window.removeEventListener('mousemove', this.onDrag);
    window.removeEventListener('mouseup', this.stopDragging);
    this.log('Dragging stopped');
  }

  formatTime(seconds: number): string {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.floor(seconds % 60);
    return `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
  }

  parseTime(time: string): number {
    const parts = time.split(':').map(part => parseInt(part, 10));
    if (parts.length !== 2 || parts.some(isNaN)) {
      this.log(`parseTime: Invalid time format received: ${time}`);
      return 0;
    }
    const totalSeconds = parts[0] * 60 + parts[1];
    this.log(`parseTime: Parsed ${time} to ${totalSeconds} seconds`);
    return totalSeconds;
  }

  pad(num: number): string {
    return num < 10 ? '0' + num : num.toString();
  }

  private logVideoState() {
    this.logGroup('VideoEditComponent: Video State');
    this.log('Start Time:', this.startTime);
    this.log('End Time:', this.endTime);
    this.log('Is Video Loaded:', this.isVideoLoaded);
    this.log('Is Playing:', this.isPlaying);
    this.log('Current Time Position:', this.currentTimePosition);
    const videoElement = this.videoElement();
    if (videoElement && videoElement.nativeElement) {
      const video = videoElement.nativeElement;
      this.log('Video Element Ready State:', video.readyState);
      this.log('Video Element Source:', video.src);
      this.log('Video Element Duration:', video.duration);
    } else {
      this.log('Video Element: Not Available');
    }
    this.logGroupEnd();
  }

  async checkFFmpegVersion() {
    console.log('FFmpeg version check is not available');
  }

  onFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      this.videoFile = input.files[0];
      this._currentVideoBlob = this.videoFile; // Store the blob from file upload
      this.loadVideo();
    }
  }

  loadVideo() {
    if (this.videoFile) {
      const videoElement = this.videoElement().nativeElement;
      const objectUrl = URL.createObjectURL(this.videoFile);
      this._objectUrls.push(objectUrl); // Track for cleanup
      this._url.set(objectUrl); // Update internal URL
      videoElement.src = objectUrl;
      videoElement.load(); // Load the new source
      videoElement.onloadedmetadata = () => {
        this.forceDurationInitialization(videoElement); // Use force init
        this.cd.detectChanges();
      };
    }
  }

  downloadVideo() {
    if (this.trimmedVideoUrl) {
      const a = document.createElement('a');
      a.href = this.trimmedVideoUrl;
      a.download = 'trimmed_video.mp4';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    }
  }

  // Removed loadFFmpeg method as service loads proactively

  toggleTrimControls() {
    this.showTrimControls = !this.showTrimControls;
    // No need to manually trigger load here, service loads in constructor
    if (this.showTrimControls) {
      this.showInfoAlert = true;
    }
  }

  closeInfoAlert() {
    this.showInfoAlert = false;
  }

  // Refactored handleTrim method
  async handleTrim(): Promise<void> {
    if (this.isDestroyed || this.trimButtonState !== 'idle') {
      return;
    }

    // Check if the trim service is loaded using the service's synchronous check
    // Use this.loaded which is updated by the subscription in ngOnInit
    if (!this.loaded) {
      this.toastService.show('Trim service is preparing. Please wait a moment.', 'info');
      // Service load is initiated in its constructor, no need to call load here.
      return; // Prevent trim attempt if not loaded
    }

    // Use the stored blob if available, otherwise show error
    const videoBlobToTrim = this._currentVideoBlob;
    if (!videoBlobToTrim) {
        console.error('No video blob available for trimming. URL:', this._url());
        this.toastService.show('Video data not available for trimming.', 'error');
        return;
    }


    this.trimButtonState = 'loading'; // State: Preparing
    // this.toggleTrimControls(); // Keep controls open during trim
    this.cd.detectChanges(); // Update UI immediately

    // Reset the long trim message state
    this.showLongTrimMessage = false;
    if (this.longTrimMessageTimeout) {
      clearTimeout(this.longTrimMessageTimeout);
    }

    // Set a timeout to show the message after 5 seconds if still processing
    this.longTrimMessageTimeout = setTimeout(() => {
      if (this.trimButtonState === 'performing' && !this.isDestroyed) {
        this.showLongTrimMessage = true;
        this.cd.detectChanges();
      }
    }, 5000);

    try {
      // No need to fetch again, use the stored blob
      console.log('Using stored video blob for trimming, size:', videoBlobToTrim.size);

      this.trimButtonState = 'performing'; // State: Trimming
      this.cd.detectChanges();

      // Use originalFilename input or derive from videoFile if available
      const originalFilename = this.originalFilename() || this.videoFile?.name || 'unknown_video.mp4';

      // Call the trim service with the blob
      console.log(`Calling videoTrimService.trimVideo with start: ${this.startTime}, end: ${this.endTime}`);
      const { blob: trimmedBlob, fileName: trimmedFileName } = await this.videoTrimService.trimVideo(
          videoBlobToTrim, // Use the stored blob
          this.startTime,
          this.endTime,
          originalFilename // Pass original filename
      );
      console.log('Video trimmed successfully by service.');

      // --- Start: Saving Logic ---
      this.savingVideo = true;
      this.cd.detectChanges();

      const userId = await this.userService.getCurrentUserId();
      if (!userId) throw new Error('User ID not found.');

      const storagePath = this.generateStoragePath(userId, trimmedFileName); // Use helper
      console.log('Uploading trimmed video to:', storagePath);

      const storageRef = ref(this.storage, storagePath);
      await uploadBytes(storageRef, trimmedBlob);
      const downloadURL = await getDownloadURL(storageRef);
      console.log('Upload complete. Download URL:', downloadURL);

      const finalUrl = this.getFinalUrl(downloadURL, storagePath); // Use helper
      console.log('Final URL:', finalUrl);

      // Update UI and state
      this.clearVideo(); // Clear old video first
      await this.delay(100); // Allow UI to update

      // Set the new URL and store the new blob
      this._url.set(finalUrl); // Set internal URL to the final storage URL
      this._currentVideoBlob = trimmedBlob; // Store the *new* trimmed blob

      // Reload video element and update timeline
      const videoElement = this.videoElement();
      if (videoElement?.nativeElement) {
          const video = videoElement.nativeElement;
          const objectUrl = URL.createObjectURL(trimmedBlob); // Create object URL for the new blob
          this._objectUrls.push(objectUrl); // Track it

          const onLoadedMetadata = () => {
              const newDuration = video.duration;
              console.log('New video duration after trim:', newDuration);
              this._duration.set(newDuration);
              this.startTime = 0; // Reset start time
              this.endTime = newDuration; // Set end time to new duration
              this.originalStartTime = 0; // Reset original times
              this.originalEndTime = newDuration;
              this.updateTimeForm();
              this.repositionScrubbers();
              video.removeEventListener('loadedmetadata', onLoadedMetadata);
              this.cd.detectChanges();

              // Emit croppedVideoSaved with correct duration
              this.croppedVideoSaved.emit({
                start: 0,
                end: newDuration,
                url: finalUrl, // Use the storage/final URL
                duration: newDuration,
                filename: storagePath // Use storage path
              });
          };
          video.addEventListener('loadedmetadata', onLoadedMetadata);
          video.src = objectUrl; // Set src to the new object URL
          video.load();
      }

      // Emit videoTrimmed event
      this.videoTrimmed.emit({ url: finalUrl, filename: storagePath });

      this.toastService.show('Video trimmed and saved successfully!', 'success');
      this.showTrimmedAlert = true;
      // --- End: Saving Logic ---

    } catch (error: any) {
        console.error('Error during trim process:', error);
        this.toastService.show(`Trim failed: ${error.message || 'Unknown error'}`, 'error');
    } finally {
         // Ensure state is reset regardless of success or failure
        if (!this.isDestroyed) {
            this.trimButtonState = 'idle';
            this.savingVideo = false;
            this.showLongTrimMessage = false;
            if (this.longTrimMessageTimeout) {
                clearTimeout(this.longTrimMessageTimeout);
                this.longTrimMessageTimeout = null;
            }
            this.cd.detectChanges();
        }
    }
  }

  // Helper method to generate storage path
  private generateStoragePath(userId: string, baseFilename: string): string {
      const filenameWithoutExt = baseFilename.replace(/\.[^/.]+$/, '');
      const extension = baseFilename.split('.').pop() || 'mp4';
      // Ensure it doesn't add _trimmed if already present
      const trimmedSuffix = '_trimmed';
      const finalFilename = filenameWithoutExt.endsWith(trimmedSuffix)
          ? `${filenameWithoutExt}.${extension}`
          : `${filenameWithoutExt}${trimmedSuffix}.${extension}`;
      // Store in 'edited' subfolder within the user's webcam recordings
      return `recordings/webcam/${userId}/edited/${finalFilename}`;
  }

  // Helper method to get final URL based on environment
  private getFinalUrl(downloadURL: string, storagePath: string): string {
      if (!environment.production && downloadURL.includes('firebasestorage.googleapis.com')) {
          const localUrl = new URL('http://127.0.0.1:9199');
          localUrl.pathname = `/v0/b/local-storage-bucket/o/${encodeURIComponent(storagePath)}`;
          localUrl.searchParams.set('alt', 'media');
          const firebaseUrl = new URL(downloadURL);
          const token = firebaseUrl.searchParams.get('token');
          if (token) {
              localUrl.searchParams.set('token', token);
          }
          return localUrl.toString();
      }
      return downloadURL;
  }


  // Helper method to extract recording ID from URL
  private getRecordingIdFromUrl(url: string): string | undefined {
    try {
      // Check if URL contains a path segment that might be a recording ID
      // Example: .../recordings/webcam/userId/timestamp.mp4
      const urlObj = new URL(url);
      const pathParts = urlObj.pathname.split('/');

      // Look for 'recordings' in the path
      const recordingsIndex = pathParts.findIndex(part => part === 'recordings');
      if (recordingsIndex >= 0 && pathParts.length > recordingsIndex + 3) {
        // The recording ID might be in the query parameters
        const params = new URLSearchParams(urlObj.search);
        if (params.has('recordingId')) {
          return params.get('recordingId') || undefined;
        }
      }

      return undefined;
    } catch (error) {
      console.warn('Error parsing URL for recording ID:', error);
      return undefined;
    }
  }

  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  // **Getter to Check if Trim is Enabled**
  get isTrimEnabled(): boolean {
    return this.startTime !== this.originalStartTime || this.endTime !== this.originalEndTime;
  }

  // **Method to Show Tooltip**
  showDisabledTooltip(): void {
    this.showTooltip = true;
    setTimeout(() => {
      this.showTooltip = false;
    }, 3000); // Tooltip disappears after 3 seconds
  }

  // **Updated Handle Trim Button Click**
  onTrimButtonClick(): void {
    if (this.isTrimEnabled) {
      this.handleTrim();
    } else {
      this.showDisabledTooltip();
    }
  }

  private getEditedFilename(originalFilename: string): string {
    const baseFilename = originalFilename.split('.')[0];
    const extension = originalFilename.split('.').pop();
    const timestamp = new Date().getTime();
    return `${baseFilename}_edited_${timestamp}.${extension}`;
  }

  // Removed initializeWorker method
  // Removed handleWorkerMessage method
  // Removed handleTrimmedVideo method

  // Update methods to use writable signals
  updateDuration(value: number) {
    console.log('VideoEditComponent: updateDuration called with value:', value);
    if (value > 0 && value !== this._duration()) {
      this._duration.set(value);
      this.endTime = value;
      this.originalEndTime = value;
      this.updateEndTime();
      this.repositionScrubbers();
      this.cd.detectChanges();
    }
  }

  updateTime(value: number) {
    this._time.set(value);
  }

  updateUrl(value: string | null) {
    if (value && value !== this._url()) {
      this._url.set(value);
      this.processVideo(value); // Call processVideo when URL input changes
    }
  }

  // Update arithmetic operations
  private calculatePosition(value: number): number {
    const duration = this._computedDuration();
    return duration > 0 ? (value / duration) * 100 : 0;
  }

  private repositionScrubbers() {
    // Skip if we're in read-only mode
    if (this.readOnly()) {
      return;
    }

    if (!this.isVideoLoaded) return;

    const duration = this._duration();
    if (!duration) return;

    const sliderContainer = this.sliderContainer();
    if (!sliderContainer?.nativeElement) return;

    // Force a style recalculation
    requestAnimationFrame(() => {
      if (this.isDestroyed || this.readOnly()) return;

      // Get scrubber elements
      const startScrubber = sliderContainer.nativeElement.querySelector('[data-scrubber="start"]') as HTMLElement;
      const endScrubber = sliderContainer.nativeElement.querySelector('[data-scrubber="end"]') as HTMLElement;

      if (!startScrubber || !endScrubber) return;

      const sliderRect = sliderContainer.nativeElement.getBoundingClientRect();
      const sliderWidth = sliderRect.width;

      // Calculate positions
      const startPercent = (this.startTime / duration) * 100;
      const endPercent = (this.endTime / duration) * 100;

      // Apply positions directly
      startScrubber.style.left = `${startPercent}%`;
      endScrubber.style.left = `${endPercent}%`;

      this.cd.detectChanges();
    });
  }

  // Update setVideoSource to prevent duplicate processing
  setVideoSource(url: string | null): void {
    if (!url) {
      console.log('Clearing video source');
      this.clearVideo(); // Use clearVideo for consistency
      return;
    }

    if (url !== this.videoUrl()) { // Compare with input URL to decide if processing needed
      console.log('Setting video source via setVideoSource:', url);
      this.processVideo(url); // Process the new URL
    } else {
      console.log('setVideoSource called with current URL, skipping reprocessing.');
      // Ensure video element src is set if it somehow got cleared
      const videoElement = this.videoElement()?.nativeElement;
      if (videoElement && !videoElement.src && this._url()) {
          videoElement.src = this._url()!; // Use internal object URL if available
          videoElement.load();
      }
    }
  }


  clearVideo(): void {
    const videoElement = this.videoElement();
    if (videoElement?.nativeElement) {
      videoElement.nativeElement.src = '';
      videoElement.nativeElement.load();
    }
    this._url.set(null);
    this._currentVideoBlob = null; // Clear blob
    this.isVideoLoaded = false;
    // Revoke any existing object URLs when clearing
    this.cleanupVideoResources();
    this.cd.detectChanges();
  }


  getDuration(): number {
    const videoElement = this.videoElement();
    // Ensure duration is a valid number
    const duration = videoElement?.nativeElement?.duration;
    return (duration && isFinite(duration)) ? duration : 0;
  }

  getCurrentTime(): number {
    const videoElement = this.videoElement();
    return videoElement?.nativeElement?.currentTime || 0;
  }
}
